import { Button } from 'UI';
import {
  PLAN_START_DOLLARS,
  TIER_LABEL,
  accentForSlug,
  cdnCoverForSlug,
  cdnIconForSlug,
  certCodeForSlug,
  formatDollars,
  formatUsd,
  planMonthlyDollars,
  planSavingsDollars,
  tierBlurb,
} from 'components/Modals/BundleBuilderModal/bundleBuilderConfig';
import { BundleBuilderCatalog } from 'components/Modals/BundleBuilderModal/useBundleBuilderCatalog';
import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { respondToWidth } from 'styles/general/respondTo';
import { typography } from 'styles/general/typography';
import { trackAmplitudeEvent } from 'utils/trackAmplitudeEvent';

import { MiniBundleBuilder } from '../useMiniBundleBuilder';

type Props = {
  builder: MiniBundleBuilder;
  catalog: BundleBuilderCatalog;
  ownedCertIds: string[];
  /** Tier of the bundle the learner is trialling, so its package can say so. */
  trialTier: string | null;
  /** Set when checkout would refuse a new package (a payment plan is running); replaces Continue. */
  blockedReason: string | null;
  isContinuing: boolean;
  error: string | null;
  onContinue: () => void;
  onOpenFullBuilder: () => void;
};

/**
 * The compact Bundle Builder at the top of Add new course.
 *
 * Bundles are not courses — a package is a choice of certifications, not one more thing to study —
 * so they get this panel instead of sixteen near-identical cards in the course grid. It keeps the
 * two decisions the full builder asks for (package, then certifications) and drops the box render
 * and the stacks, so it fits above the fold. Learners who want the full experience can open the
 * full builder from here, pre-loaded with the same picks.
 */
export const BundleBuilderPanel = forwardRef<HTMLElement, Props>(
  (
    { builder, catalog, ownedCertIds, trialTier, blockedReason, isContinuing, error, onContinue, onOpenFullBuilder },
    ref,
  ) => {
    const { tiers, active, isMaster, lockedIds, picks, slots, selectedIds, isComplete, matchedBundle, certById } =
      builder;

    const remaining = slots - picks.length;
    const prices = matchedBundle?.prices ?? active?.prices;
    const monthly = planMonthlyDollars(prices?.installmentAmount, prices?.commitmentPaymentAmount);
    const savings = planSavingsDollars(prices?.purchaseAmount, prices?.installmentAmount);

    const hint = isMaster
      ? 'All six certifications are in this package'
      : remaining > 0
      ? `Pick ${remaining} more`
      : slots === 1
      ? 'Tap another certification to swap'
      : 'Tap an included certification to swap it out';

    return (
      <Root ref={ref} aria-labelledby="bundle-builder-title">
        <Header>
          <div>
            <BestValue>Best value</BestValue>
            <Title id="bundle-builder-title">Build your certification bundle</Title>
            <Subtitle>CPT is in every package. Pick your specialties and see exactly which courses you get.</Subtitle>
          </div>
          {!blockedReason && (
            <FullBuilderLink type="button" onClick={onOpenFullBuilder}>
              Open full builder →
            </FullBuilderLink>
          )}
        </Header>

        <Step>
          <StepLabel>
            <StepNumber>1</StepNumber>
            Choose a package
          </StepLabel>

          <Tiers role="radiogroup" aria-label="Package">
            {tiers.map((entry) => {
              const selected = entry.tier === active?.tier;
              const tierMonthly = planMonthlyDollars(
                entry.prices.installmentAmount,
                entry.prices.commitmentPaymentAmount,
              );

              return (
                <TierButton
                  key={entry.tier}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  $selected={selected}
                  onClick={() => {
                    if (!selected) {
                      trackAmplitudeEvent('Bundle Package Selected', { tier: entry.tier, source: 'add_new_course' });
                    }
                    builder.chooseTier(entry);
                  }}>
                  <TierHead>
                    <TierName>{TIER_LABEL[entry.tier]}</TierName>
                    {trialTier === entry.tier && <TrialTag>Your trial</TrialTag>}
                  </TierHead>
                  <TierBlurb $selected={selected}>{tierBlurb(entry.tier, entry.memberCourseIds.length)}</TierBlurb>
                  <TierIcons aria-hidden>
                    {entry.memberSlugs.map((slug) => (
                      <img key={slug} src={cdnIconForSlug(slug)} alt="" loading="lazy" />
                    ))}
                  </TierIcons>
                  <TierPrice>{formatUsd(entry.prices.purchaseAmount)}</TierPrice>
                  {tierMonthly !== undefined && (
                    <TierPlan>
                      or {formatDollars(PLAN_START_DOLLARS)} today, then {formatDollars(tierMonthly)}/mo
                    </TierPlan>
                  )}
                </TierButton>
              );
            })}
          </Tiers>
        </Step>

        <Step>
          <StepLabel>
            <StepNumber>2</StepNumber>
            {isMaster ? 'Everything included' : 'Pick your certifications'}
            <StepHint $done={isComplete}>{hint}</StepHint>
          </StepLabel>

          <Covers>
            {catalog.certs.map((cert) => {
              const locked = lockedIds.includes(cert.courseId);
              const included = locked || picks.includes(cert.courseId);
              // A one-slot package swaps on click, so its covers never go inert.
              const full = !included && remaining <= 0 && slots !== 1;
              const accent = accentForSlug(cert.slug);
              const cover = cert.coverImageUrl || cdnCoverForSlug(cert.slug);
              const owned = ownedCertIds.includes(cert.courseId);

              return (
                <Cover
                  key={cert.courseId}
                  type="button"
                  disabled={locked || full}
                  aria-pressed={included}
                  aria-label={`${locked ? 'Included' : included ? 'Remove' : 'Add'} ${cert.name}`}
                  $included={included}
                  $locked={locked}
                  $accent={accent}
                  onClick={() => builder.togglePick(cert.courseId)}>
                  <CoverArt $dim={!included}>
                    {cover ? (
                      <img src={cover} alt="" loading="lazy" />
                    ) : (
                      <CoverPlaceholder $accent={accent}>{certCodeForSlug(cert.slug)}</CoverPlaceholder>
                    )}
                    {included && (
                      <CoverState $accent={accent}>{locked && !isMaster ? 'Always in' : 'Included'}</CoverState>
                    )}
                  </CoverArt>
                  <CoverCode>{certCodeForSlug(cert.slug) || cert.name}</CoverCode>
                  <CoverName>{cert.name}</CoverName>
                  {owned && <Owned>You own this</Owned>}
                </Cover>
              );
            })}
          </Covers>
        </Step>

        <Summary>
          <SummaryText>
            <SummaryTitle>
              {active ? TIER_LABEL[active.tier] : 'Choose a package'}
              {active && (
                <span>
                  {' '}
                  · {selectedIds.length} of {builder.certCount} certifications
                </span>
              )}
            </SummaryTitle>
            <Included aria-label="Courses in this bundle">
              {selectedIds.map((id) => {
                const cert = certById.get(id);
                if (!cert) return null;
                return (
                  <IncludedChip key={id} $accent={accentForSlug(cert.slug)}>
                    {cert.slug && <img src={cdnIconForSlug(cert.slug)} alt="" loading="lazy" />}
                    {cert.name}
                  </IncludedChip>
                );
              })}
            </Included>
          </SummaryText>

          <SummaryAction>
            {prices?.purchaseAmount !== undefined && (
              <SummaryPrice>
                <strong>{formatUsd(prices.purchaseAmount)}</strong>
                {monthly !== undefined && (
                  <PlanLine>
                    or {formatDollars(PLAN_START_DOLLARS)} today, then {formatDollars(monthly)}/mo
                  </PlanLine>
                )}
                {savings !== undefined && <Saving>Pay in full and save {formatDollars(savings)}</Saving>}
              </SummaryPrice>
            )}
            <ContinueButton
              size="middle"
              disabled={!isComplete || isContinuing || Boolean(blockedReason)}
              isLoading={isContinuing}
              onClick={onContinue}>
              {isComplete ? 'Continue' : `Pick ${remaining} more`}
            </ContinueButton>
          </SummaryAction>
        </Summary>

        {(blockedReason || error) && <Notice role={error ? 'alert' : undefined}>{blockedReason || error}</Notice>}
      </Root>
    );
  },
);

BundleBuilderPanel.displayName = 'BundleBuilderPanel';

/** Placeholder with the panel's footprint, so the course list does not jump when the catalogue lands. */
export const BundleBuilderPanelSkeleton = () => (
  <Root aria-busy="true" aria-label="Loading bundles">
    <SkeletonBlock style={{ width: 220, height: 28 }} />
    <SkeletonBlock style={{ width: '60%', height: 16 }} />
    <SkeletonRow>
      <SkeletonBlock style={{ height: 120 }} />
      <SkeletonBlock style={{ height: 120 }} />
      <SkeletonBlock style={{ height: 120 }} />
    </SkeletonRow>
    <SkeletonBlock style={{ height: 150 }} />
  </Root>
);

const Root = styled.section`
  ${({ theme: { colors } }) => css`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 22px;
    padding: 28px;
    border-radius: 20px;
    border: 1px solid ${colors.primary[4]};
    background: linear-gradient(160deg, ${colors.primary[5]} 0%, ${colors.primary[6]} 38%, ${colors.neutrals[11]} 100%);
    box-shadow: ${colors.shadow[2]};
    scroll-margin-top: 72px;

    ${respondToWidth.sm`
      gap: 18px;
      padding: 20px 16px;
      border-radius: 16px;
    `}
  `}
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;

  ${respondToWidth.sm`
    flex-direction: column;
    gap: 8px;
  `}
`;

const BestValue = styled.span`
  ${({ theme: { colors } }) => css`
    display: inline-block;
    margin-bottom: 8px;
    padding: 3px 10px;
    border-radius: 999px;
    ${typography.footnote_bold_12};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${colors.neutrals[11]};
    background: ${colors.primary[1]};
  `}
`;

const Title = styled.h2`
  margin: 0;
  ${typography.title_4_bold_24};
  color: ${({ theme: { colors } }) => colors.neutrals[1]};

  ${respondToWidth.sm`
    ${typography.title_5_bold_20};
  `}
`;

const Subtitle = styled.p`
  margin: 4px 0 0;
  ${typography.body_basic_regular_14};
  color: ${({ theme: { colors } }) => colors.neutrals[2]};
`;

const FullBuilderLink = styled.button`
  ${({ theme: { colors } }) => css`
    flex-shrink: 0;
    padding: 8px 0;
    border: none;
    background: none;
    cursor: pointer;
    ${typography.body_basic_bold_14};
    color: ${colors.primary[1]};

    &:hover {
      text-decoration: underline;
    }
  `}
`;

const Step = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StepLabel = styled.h3`
  ${({ theme: { colors } }) => css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin: 0;
    ${typography.body_large_bold_16};
    color: ${colors.neutrals[1]};
  `}
`;

const StepNumber = styled.span`
  ${({ theme: { colors } }) => css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    ${typography.footnote_bold_12};
    color: ${colors.neutrals[11]};
    background: ${colors.neutrals[1]};
  `}
`;

const StepHint = styled.span<{ $done: boolean }>`
  ${({ theme: { colors }, $done }) => css`
    ${typography.subhead_semibold_13};
    color: ${$done ? colors.neutrals[3] : colors.primary[1]};
  `}
`;

const Tiers = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  /* One package per row on phones: three squeezed columns wrapped every name and blurb. */
  ${respondToWidth.sm`
    grid-template-columns: 1fr;
    gap: 8px;
  `}
`;

const TierButton = styled.button<{ $selected: boolean }>`
  ${({ theme: { colors }, $selected }) => css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    min-width: 0;
    padding: 14px 16px;
    text-align: left;
    border-radius: 14px;
    cursor: pointer;
    background: ${colors.neutrals[11]};
    border: 2px solid ${$selected ? colors.primary[1] : colors.neutrals[8]};
    box-shadow: ${$selected ? `0 8px 22px ${colors.primary[5]}` : 'none'};
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      border-color: ${$selected ? colors.primary[1] : colors.neutrals[6]};
    }

    /* A row: what the package is on the left, what it costs on the right. */
    ${respondToWidth.sm`
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      grid-template-areas:
        'head price'
        'blurb plan'
        'icons plan';
      column-gap: 12px;
      row-gap: 2px;
      align-items: center;
      padding: 12px 14px;
      border-radius: 12px;
    `}
  `}
`;

const TierHead = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  width: 100%;

  ${respondToWidth.sm`
    grid-area: head;
    justify-content: flex-start;
  `}
`;

const TierName = styled.span`
  ${typography.body_large_extrabold_16};
  color: ${({ theme: { colors } }) => colors.neutrals[1]};
`;

const TrialTag = styled.span`
  ${({ theme: { colors } }) => css`
    flex-shrink: 0;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 800;
    line-height: 1.2;
    text-transform: uppercase;
    color: ${colors.system.green};
    background: ${colors.system.lightGreen};
  `}
`;

const TierBlurb = styled.span<{ $selected: boolean }>`
  ${({ theme: { colors }, $selected }) => css`
    font-size: 11px;
    font-weight: 800;
    line-height: 1.3;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${$selected ? colors.primary[1] : colors.neutrals[3]};

    ${respondToWidth.sm`
      grid-area: blurb;
    `}
  `}
`;

const TierIcons = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  margin: 2px 0;

  img {
    height: 12px;
    width: auto;
  }

  ${respondToWidth.sm`
    grid-area: icons;
  `}
`;

const TierPrice = styled.span`
  ${typography.title_5_bold_20};
  color: ${({ theme: { colors } }) => colors.neutrals[1]};

  ${respondToWidth.sm`
    grid-area: price;
    justify-self: end;
  `}
`;

const TierPlan = styled.span`
  ${typography.footnote_semibold_12};
  color: ${({ theme: { colors } }) => colors.system.green};

  ${respondToWidth.sm`
    grid-area: plan;
    align-self: start;
    justify-self: end;
    max-width: 120px;
    text-align: right;
  `}
`;

const Covers = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px;

  ${respondToWidth.ls`
    grid-template-columns: repeat(3, minmax(0, 1fr));
  `}

  ${respondToWidth.sm`
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  `}
`;

const Cover = styled.button<{ $included: boolean; $locked: boolean; $accent: string }>`
  ${({ theme: { colors }, $included, $locked, $accent, disabled }) => css`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    min-width: 0;
    padding: 12px 8px 10px;
    border-radius: 14px;
    text-align: center;
    background: ${colors.neutrals[11]};
    border: 2px solid ${$included ? $accent : colors.neutrals[8]};
    cursor: ${disabled ? 'default' : 'pointer'};
    opacity: ${disabled && !$included ? 0.55 : 1};
    transition: border-color 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;

    ${$included &&
    !$locked &&
    css`
      box-shadow: 0 6px 18px ${$accent}33;
    `}

    &:not(:disabled):hover {
      border-color: ${$included ? $accent : colors.neutrals[6]};
    }

    &:focus-visible {
      outline: 2px solid ${$accent};
      outline-offset: 2px;
    }
  `}
`;

const CoverArt = styled.span<{ $dim: boolean }>`
  ${({ theme: { colors }, $dim }) => css`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 72px;
    aspect-ratio: 363 / 470;
    border-radius: 6px;
    overflow: hidden;
    background: ${colors.neutrals[9]};

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: filter 0.2s ease;
      filter: ${$dim ? 'saturate(0.5) opacity(0.65)' : 'none'};
    }
  `}
`;

const CoverPlaceholder = styled.span<{ $accent: string }>`
  ${typography.body_basic_extrabold_14};
  color: ${({ $accent }) => $accent};
`;

const CoverState = styled.span<{ $accent: string }>`
  position: absolute;
  left: 50%;
  bottom: 4px;
  transform: translateX(-50%);
  padding: 2px 5px;
  border-radius: 4px;
  white-space: nowrap;
  font-size: 9px;
  font-weight: 800;
  line-height: 1.2;
  text-transform: uppercase;
  color: #fff;
  background: ${({ $accent }) => $accent};
`;

const CoverCode = styled.span`
  margin-top: 4px;
  ${typography.body_basic_extrabold_14};
  color: ${({ theme: { colors } }) => colors.neutrals[1]};
`;

const CoverName = styled.span`
  width: 100%;
  ${typography.footnote_regular_12};
  color: ${({ theme: { colors } }) => colors.neutrals[3]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Owned = styled.span`
  ${({ theme: { colors } }) => css`
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 800;
    line-height: 1.2;
    text-transform: uppercase;
    color: ${colors.system.green};
    background: ${colors.system.lightGreen};
  `}
`;

const Summary = styled.div`
  ${({ theme: { colors } }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    padding: 16px 20px;
    border-radius: 14px;
    background: ${colors.neutrals[11]};
    border: 1px solid ${colors.neutrals[8]};

    ${respondToWidth.sm`
      flex-direction: column;
      align-items: stretch;
      gap: 14px;
      padding: 14px;
    `}
  `}
`;

const SummaryText = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SummaryTitle = styled.div`
  ${({ theme: { colors } }) => css`
    ${typography.body_large_bold_16};
    color: ${colors.neutrals[1]};

    span {
      ${typography.body_basic_medium_14};
      color: ${colors.neutrals[3]};
    }
  `}
`;

const Included = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const IncludedChip = styled.span<{ $accent: string }>`
  ${({ theme: { colors }, $accent }) => css`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px 4px 8px;
    border-radius: 999px;
    ${typography.footnote_semibold_12};
    color: ${colors.neutrals[1]};
    background: ${$accent}14;
    border: 1px solid ${$accent}40;

    img {
      height: 11px;
      width: auto;
    }
  `}
`;

const SummaryAction = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 16px;

  ${respondToWidth.sm`
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  `}
`;

const SummaryPrice = styled.div`
  ${({ theme: { colors } }) => css`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    text-align: right;

    strong {
      ${typography.title_5_bold_20};
      color: ${colors.neutrals[1]};
    }

    ${respondToWidth.sm`
      align-items: flex-start;
      text-align: left;
    `}
  `}
`;

const PlanLine = styled.span`
  ${typography.footnote_regular_12};
  color: ${({ theme: { colors } }) => colors.neutrals[2]};
`;

const Saving = styled.span`
  ${typography.footnote_bold_12};
  color: ${({ theme: { colors } }) => colors.system.green};
`;

const ContinueButton = styled(Button)`
  width: auto;
  min-width: 160px;

  ${respondToWidth.sm`
    width: 100%;
  `}
`;

const Notice = styled.p`
  ${({ theme: { colors } }) => css`
    margin: -8px 0 0;
    padding: 10px 14px;
    border-radius: 10px;
    ${typography.body_basic_medium_14};
    color: ${colors.neutrals[1]};
    background: ${colors.system.lightYellow};
    border: 1px solid ${colors.system.yellow}66;
  `}
`;

const SkeletonBlock = styled.div`
  width: 100%;
  border-radius: 12px;
  background: ${({ theme: { colors } }) => colors.neutrals[9]};
  animation: add-course-pulse 1.4s ease-in-out infinite;

  @keyframes add-course-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.55;
    }
  }
`;

const SkeletonRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
`;
